import type {FlushSignal} from 'src/internal/common/FlushController';
import type {
  FetchFn,
  TransferableEvent,
  Transport,
} from 'src/internal/transport/types';

const BATCH_TIMEOUT = 3_000;
// Browsers enforce a cumulative 64 KiB payload size limit across all pending keepalive requests per page.
// We send a keepalive request on flush signal only, so a transport cannot accumulate more than 64 KiB.
// Important: we create one batching transport per service, so in theory we should track overall payload size
// across all created transports and flush if necessary.
const MAX_PAYLOAD_BYTES = 64_000;

export type BatchingTransportConfig<E extends TransferableEvent> = {
  fetch: FetchFn;
  service: string;
  flushSignal?: FlushSignal;
  getUrl: () => string;
  serializeBatchBody: (service: string, events: E[]) => string;
  contentType: string;
  batchSize: number;
};

export default function createBatchingTransport<E extends TransferableEvent>(
  config: BatchingTransportConfig<E>,
): Transport<E> {
  let buffer: E[] = [];
  let batchTimer: ReturnType<typeof setTimeout> | null = null;

  const getPayloadSize = (events: E[]): number => {
    // Match analytics-js BatchLogger.getPayloadSizeInBytes (Blob byte length).
    // eslint-disable-next-line compat/compat -- mirrors batchLogger payload sizing
    return new Blob([config.serializeBatchBody(config.service, events)]).size;
  };

  const clearTimer = (): void => {
    if (batchTimer) {
      clearTimeout(batchTimer);
      batchTimer = null;
    }
  };

  const resetBuffer = (): void => {
    buffer = [];
    clearTimer();
  };

  const flush = (keepalive = false): void => {
    if (buffer.length === 0) {
      return;
    }

    const events = buffer;
    resetBuffer();

    config.fetch(config.getUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': config.contentType,
      },
      mode: 'no-cors',
      keepalive,
      body: config.serializeBatchBody(config.service, events),
    });
  };

  config.flushSignal?.addEventListener(() => {
    flush(true);
  });

  return {
    send(event: E): void {
      if (getPayloadSize([...buffer, event]) >= MAX_PAYLOAD_BYTES) {
        flush();
      }

      buffer.push(event);

      if (buffer.length >= config.batchSize) {
        flush();
      } else if (!batchTimer) {
        batchTimer = setTimeout(() => {
          flush();
        }, BATCH_TIMEOUT);
      }
    },
  };
}
